import { useEffect, useState, useRef } from "react";
import {
  View,
  Edit,
  Trash,
  Plus,
  Printer,
  FileDown,
  Upload,
  Search,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Video,
  School,
  Network,
  ChevronDown
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import toast, { Toaster } from "react-hot-toast";

import Header from "../../components/Header";
import ITEquipmentModal from "../components/ITEquipementModal";
import ITBulkUploadModal from "../components/ITBulkUploadModal";
import ITViewModal from "../components/ITViewModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";

import api from "../../connector/api";

const menuItems = [
    {
      name: "Rooms Management",
      icon: <School size={15} />,
      children: [
        { name: "Room Equipment", path: "/inventory/room-equipment" },
      ],
    },
    {
      name: "IT Infrastructure",
      icon: <Network size={15} />,
      children: [
        { name: "CCTV Camera", path: "/inventory/cctv-camera" },
        { name: "Desktop Computer", path: "/inventory/desktop-computer" },
        { name: "Media Equipment", path: "/inventory/media-equipment" },
        { name: "Network Equipment", path: "/inventory/network-equipment" },
      ],
    },
];

export default function CCTVCamera() {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [activeDropdown, setActiveDropdown] = useState(null); 
  const navRef = useRef(null);

  const [formData, setFormData] = useState({
    id: null,
    description: "",
    category: "",
    serial_number: "",
    cost: "",
    date_of_purchase: "",
    location: "",
    responsible_person: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch equipment
  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const res = await api.get("it-equipment/");
      
      // 1. Safe-check: extract the array whether it's paginated (.results) or a direct array
      const rawData = Array.isArray(res.data) ? res.data : (res.data.results || []);
      
      // 2. Filter the extracted array
      const networkEquipment = rawData.filter(
        (eq) => eq.category === "Desktop Computer"
      );
      
      setEquipment(networkEquipment);
    } catch (err) {
      console.error("API Error details:", err);
      toast.error("Failed to fetch equipment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEquipment();
  }, []);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await api.put(`it-equipment/${formData.id}/`, formData);
        toast.success("Equipment updated!");
      } else {
        await api.post("it-equipment/", formData);
        toast.success("Equipment added!");
      }
      fetchEquipment();
      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      toast.error("Error saving equipment");
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      await api.delete(`it-equipment/${id}/`);
      toast.success("Equipment deleted");
      fetchEquipment();
    } catch (err) {
      toast.error("Failed to delete equipment");
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      description: "",
      category: "",
      serial_number: "",
      cost: "",
      date_of_purchase: "",
      location: "",
      responsible_person: "",
    });
  };

  // Filtered equipment by search
  const filteredEquipment = equipment.filter(
    (eq) =>
      eq.description.toLowerCase().includes(search.toLowerCase()) ||
      eq.category.toLowerCase().includes(search.toLowerCase()) ||
      eq.serial_number.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredEquipment.length / itemsPerPage);
  const currentItems = filteredEquipment.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Handle Export
  const handleExport = () => {
    if (!equipment.length) {
      toast.error("No data to export!");
      return;
    }

    const exportData = equipment.map((eq) => ({
      Description: eq.description,
      Category: eq.category,
      Model: eq.model,
      Serial: eq.serial_number,
      Cost: eq.cost,
      "Purchase Date": eq.date_of_purchase,
      Location: eq.location,
      Responsible: eq.responsible_person_name || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Network Equipment");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Network_Equipment_${new Date().toISOString().split("T")[0]}.xlsx`);

    toast.success("Excel exported successfully!");
  };

  // Handle Print
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleString();

    printWindow.document.write(`
      <html>
        <head>
          <title>Network Equipment Report</title>
          <link href="https://cdn.jsdelivr.net/npm/tailwindcss@3.3.2/dist/tailwind.min.css" rel="stylesheet">
          <style>
            @media print {
              body { -webkit-print-color-adjust: exact; font-family: sans-serif; }
              table { page-break-inside: auto; border-collapse: collapse; }
              tr { page-break-inside: avoid; page-break-after: auto; }
              th, td { border: 1px solid #e2e8f0; padding: 0.5rem; }
            }
            th { background-color: #f8fafc; font-weight: bold; }
          </style>
        </head>
        <body class="p-6">
          <div class="flex justify-between items-center mb-6">
            <div class="flex items-center gap-4">
              <img src="/logo.png" alt="Logo" class="h-12 w-12 object-contain"/>
              <div>
                <p class="text-sm font-semibold">Network Equipment Inventory Report</p>
              </div>
            </div>
            <div class="text-sm text-gray-700">
              Printed: ${formattedDate}
            </div>
          </div>
          <table class="min-w-full border border-gray-300 text-sm">
            <thead>
              <tr>
                <th>Description</th>
                <th>Serial</th>
                <th>Cost</th>
                <th>Purchase Date</th>
                <th>Location</th>
                <th>Responsible</th>
              </tr>
            </thead>
            <tbody>
              ${equipment
                .map(
                  (eq) => `
                <tr>
                  <td>${eq.description}</td>
                  <td>${eq.serial_number}</td>
                  <td>$${eq.cost}</td>
                  <td>${eq.date_of_purchase}</td>
                  <td>${eq.location}</td>
                  <td>${eq.responsible_person_name || "N/A"}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          <footer class="mt-8 text-xs text-gray-400">
            &copy; ${currentDate.getFullYear()} Cambodia Adventist International School. All rights reserved.
          </footer>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div className="min-h-screen bg-slate-50/60 font-dynamic-title flex flex-col">
      {/* Full width main top layout block */}
      <Header />

      {/* Structured Max Width Workspace Container */}
      <div className="max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-8 flex flex-col gap-6 flex-1">
        {/* ================= REFINED INLINE NAVIGATION SUB-BAR ================= */}
                <div className="bg-white border border-slate-200/80 shadow-xs rounded-xl px-4 py-2 flex items-center justify-between relative" ref={navRef}>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 border-r border-slate-200 pr-3 mr-1 hidden sm:inline">
                            Modules
                        </span>
                        
                        {menuItems.map((item) => {
                            const isDropdownOpen = activeDropdown === item.name;
                            return (
                                <div key={item.name} className="relative">
                                    <button
                                        onClick={() => setActiveDropdown(isDropdownOpen ? null : item.name)}
                                        className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                                            isDropdownOpen 
                                            ? 'bg-[#002855] bg-opacity-80 text-white shadow-sm' 
                                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                        }`}
                                    >
                                        <span className={isDropdownOpen ? 'text-blue-400' : 'text-slate-400'}>
                                            {item.icon}
                                        </span>
                                        {item.name}
                                        <ChevronDown size={13} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180 text-blue-400' : 'text-slate-400'}`} />
                                    </button>

                                    {/* Anchored Floating Secondary Card Menu */}
                                    {isDropdownOpen && (
                                        <div className="absolute left-0 mt-2 w-56 bg-white border border-slate-200/90 rounded-xl shadow-xl z-50 p-1.5 animate-in fade-in slide-in-from-top-2 duration-150">
                                            {item.children.map((child) => (
                                                <a
                                                    key={child.name}
                                                    href={child.path}
                                                    className="flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-[#002855] rounded-lg transition-all group"
                                                >
                                                    <span>{child.name}</span>
                                                    <ChevronRight size={12} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-all transform translate-x-[-4px] group-hover:translate-x-0" />
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Meta Status Indicator Block */}
                    <div className="hidden md:flex items-center gap-2 text-[10px] font-mono font-bold text-slate-400 bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        SYSTEM NODE LOGS ACTIVE
                    </div>
                </div>
        {/* Module Title Header Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200/60 pb-5">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-base font-bold text-slate-900 tracking-tight">Desktop Computer</h1>
              <p className="text-[11px] text-slate-500 mt-0.5">Manage, track, log, and audit physical surveillance endpoints asset indices.</p>
            </div>
          </div>

          {/* Core Creation/Action Hub Elements Instead of Multi-step Menu list items */}
          <div className="flex items-center gap-2 self-end sm:self-center">
            <button 
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 rounded-xl shadow-xs transition-all cursor-pointer active:scale-98"
            >
              <Plus size={14} />
              Add Unit Entry
            </button>
            <button 
              onClick={() => setBulkModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl shadow-xs hover:bg-slate-50 transition-all cursor-pointer"
            >
              <Upload size={14} className="text-slate-400" />
              Bulk Import
            </button>
          </div>
        </div>

        {/* Global Toolbar Panel Grid Container */}
        <div className="bg-white border border-slate-200/80 shadow-xs rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
          
          {/* Functional Search Field */}
          <div className="relative max-w-md w-full">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <input
              type="text"
              placeholder="Search by SKU, Description, Location, Serial String..."
              className="w-full pl-10 pr-4 py-2 text-xs font-medium text-slate-700 bg-slate-50/50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#002855]/5 focus:border-[#002855] outline-none transition-all"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* Export Handling Group Options */}
          <div className="flex items-center gap-2 self-end md:self-auto">
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-emerald-700 bg-emerald-50/50 hover:bg-emerald-50 border border-emerald-100 rounded-xl transition-all cursor-pointer"
            >
              <FileDown size={14} />
              Export Sheet
            </button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-all cursor-pointer"
            >
              <Printer size={14} className="text-slate-400" />
              Print Report
            </button>
          </div>
        </div>

        {/* Dynamic Asset Registry Interactive Feed (Table Block) */}
        <div className="bg-white rounded-xl border border-slate-200/70 shadow-xs overflow-hidden">
          {loading ? (
            <div className="p-12 flex flex-col items-center justify-center gap-2 text-slate-400">
              <div className="w-6 h-6 border-2 border-t-transparent border-[#002855] rounded-full animate-spin" />
              <span className="text-xs font-medium mt-1">Retrieving endpoint catalog matrix...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200 text-[10px] font-black uppercase tracking-wider text-slate-400">
                    <th className="p-4 pl-6">Unit Description</th>
                    <th className="p-4 hidden md:table-cell">Hardware Serial</th>
                    <th className="p-4">Acquisition Cost</th>
                    <th className="p-4 hidden lg:table-cell">Purchase Date</th>
                    <th className="p-4 hidden lg:table-cell">Assigned Location</th>
                    <th className="p-4 hidden lg:table-cell">Custodian Account</th>
                    <th className="p-4 pr-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-600 font-medium">
                  {currentItems.length > 0 ? (
                    currentItems.map((eq) => (
                      <tr key={eq.id} className="hover:bg-slate-50/40 transition-colors group">
                        <td className="p-4 pl-6 font-bold text-slate-800">{eq.description}</td>
                        <td className="p-4 hidden md:table-cell font-mono text-[11px] text-slate-500 font-semibold">{eq.serial_number}</td>
                        <td className="p-4 font-semibold text-slate-900">${Number(eq.cost).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                        <td className="p-4 hidden lg:table-cell text-slate-500">{eq.date_of_purchase}</td>
                        <td className="p-4 hidden lg:table-cell">
                          <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-700 font-semibold rounded-md text-[10px]">
                            {eq.location}
                          </span>
                        </td>
                        <td className="p-4 hidden lg:table-cell text-slate-500">
                          {eq.responsible_person_name || <span className="text-slate-300 italic font-normal">Unassigned</span>}
                        </td>
                        <td className="p-4 pr-6 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => {
                                setSelectedEquipment(eq);
                                setViewModalOpen(true);
                              }}
                              title="View Details"
                              className="text-slate-400 hover:text-[#002855] p-1.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                            >
                              <View size={15} />
                            </button>
                            <button
                              onClick={() => {
                                setFormData(eq);
                                setIsModalOpen(true);
                              }}
                              title="Edit Entry"
                              className="text-slate-400 hover:text-amber-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                            >
                              <Edit size={15} />
                            </button>
                            <button
                              onClick={() => {
                                setItemToDelete(eq);
                                setDeleteModalOpen(true);
                              }}
                              title="Remove Entry"
                              className="text-slate-400 hover:text-rose-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                            >
                              <Trash size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-12 text-slate-400 font-medium">
                        <SlidersHorizontal size={24} className="mx-auto mb-2 text-slate-300" />
                        No corresponding surveillance nodes discovered matching parameters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Central Bounded Framework Pagination Sub-Footer */}
              {totalPages > 1 && (
                <div className="p-4 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between gap-4 text-xs font-semibold text-slate-500">
                  <button
                    onClick={handlePrev}
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border border-slate-200 bg-white enabled:hover:bg-slate-50 enabled:text-slate-700 disabled:opacity-40 transition-all cursor-pointer"
                  >
                    <ChevronLeft size={14} />
                    Previous
                  </button>
                  <span className="font-mono text-[11px]">
                    Page <span className="text-slate-800 font-bold">{currentPage}</span> of <span className="text-slate-800 font-bold">{totalPages}</span>
                  </span>
                  <button
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border border-slate-200 bg-white enabled:hover:bg-slate-50 enabled:text-slate-700 disabled:opacity-40 transition-all cursor-pointer"
                  >
                    Next
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Component Level Configuration Dialog Popups */}
      <ITEquipmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
      />
      <ITBulkUploadModal isOpen={bulkModalOpen} onClose={() => setBulkModalOpen(false)} />
      <ITViewModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        equipment={selectedEquipment}
      />
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        itemName={itemToDelete?.description || "this item"}
        onConfirm={() => {
          handleDelete(itemToDelete.id);
          setDeleteModalOpen(false);
        }}
      />

      <Toaster position="top-right" />
    </div>
  );
}